import React, { useCallback } from 'react';
import { KatButton } from '@amzn/katal-react';
import { useAppConfigDeployer } from './useAppConfigDeployer';
import JsonDiffView from './JsonDiffView';
import Timestamp from '../common/Timestamp';

interface Props {
    highlightedChangeId: string | null;
    deployer: ReturnType<typeof useAppConfigDeployer>;
    userAlias: string;
}

const ConfigDeployerTab: React.FC<Props> = ({ highlightedChangeId, deployer, userAlias }) => {
    const focusOnMount = useCallback((el: HTMLDivElement | null) => el?.focus(), []);

    const {
        deployerRows, loadingDeployer, deployerError, deployerSuccess,
        actionLoading, showRejectModal, setShowRejectModal, copiedId,
        reviewDiffChangeId, reviewDiffLoading, reviewDiffOldData, reviewDiffNewData,
        handleViewDiff, handleShareLink, handleApprove, handleReject,
        closeDiffModal,
    } = deployer;

    const deployerTableHead = (
        <thead>
            <tr>
                <th>ID</th><th>Use Case</th><th>Requester</th><th>Reviewer</th>
                <th>Creation Date</th><th>Region</th><th>Version</th><th>Status</th><th>Actions</th>
            </tr>
        </thead>
    );

    return (
        <>
            <div className="tab-pane">
                <div className="info-banner">
                    <svg className="info-banner__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8" cy="8" r="7.5" stroke="#0073bb"/>
                        <path d="M8 7v5" stroke="#0073bb" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="8" cy="4.5" r="0.75" fill="#0073bb"/>
                    </svg>
                    Approved config changes will be automatically deployed to the selected environment.
                </div>

                {deployerError && <div className="error-banner">{deployerError}</div>}
                {deployerSuccess && <div className="success-banner">{deployerSuccess}</div>}

                {loadingDeployer && (
                    <div className="loading-overlay">
                        <div className="spinner" />
                        <span>Loading change requests</span>
                    </div>
                )}

                {!loadingDeployer && deployerRows.length === 0 && (
                    <table className="config-table deployer-table">
                        {deployerTableHead}
                        <tbody>
                            <tr><td colSpan={9} className="empty-state">No change requests found. Save a version from the Config Editor to create one</td></tr>
                        </tbody>
                    </table>
                )}

                {!loadingDeployer && deployerRows.length > 0 && (
                    <table className="config-table deployer-table">
                        {deployerTableHead}
                        <tbody>
                            {deployerRows.map(row => (
                                <tr key={row.changeId} className={`${row.isPending ? 'deployer-row-pending' : ''} ${highlightedChangeId === row.changeId ? 'deployer-row-highlighted' : ''}`}>
                                    <td className="col-id-link" title="Click to copy review link"
                                        onClick={() => handleShareLink(row.changeId)}
                                        style={{ cursor: 'pointer' }}>
                                        {copiedId === row.changeId ? '✓ Copied!' : row.changeId}
                                    </td>
                                    <td>{row.useCase}</td>
                                    <td className="col-alias">{row.requestor}</td>
                                    <td className="col-alias">{row.reviewer}</td>
                                    <td className="col-date"><Timestamp date={row.createdAt} /></td>
                                    <td>{row.region}</td>
                                    <td>
                                        {row.isPending
                                            ? <span>v{row.fromVersion} → ?</span>
                                            : row.status === 'APPROVED'
                                                ? <span>v{row.fromVersion} → v{row.toVersion}</span>
                                                : <span>—</span>
                                        }
                                    </td>
                                    <td>
                                        <span className={`status-badge status-${row.status.toLowerCase().replace(/_/g, '-')}`}>
                                            {row.status === 'WAITING_FOR_APPROVAL' ? 'Pending' : row.status === 'APPROVED' ? 'Approved' : 'Rejected'}
                                        </span>
                                    </td>
                                    <td className="col-actions">
                                        {row.isPending && (
                                            <div className="action-buttons">
                                                <KatButton variant="secondary"
                                                    label={reviewDiffLoading && reviewDiffChangeId === row.changeId ? 'Loading...' : 'Diff'}
                                                    onClick={() => handleViewDiff(row)}
                                                    disabled={reviewDiffLoading} />
                                                <span title={row.requestor === userAlias ? 'You cannot approve your own change request' : undefined}>
                                                    <KatButton variant="primary" label={actionLoading === row.changeId ? '...' : 'Approve'}
                                                        onClick={() => handleApprove(row.changeId, row.requestor)}
                                                        disabled={actionLoading !== null || row.requestor === userAlias} />
                                                </span>
                                                <KatButton variant="danger" label="Reject"
                                                    onClick={() => setShowRejectModal(row.changeId)}
                                                    disabled={actionLoading !== null} />
                                            </div>
                                        )}
                                        {!row.isPending && row.proposedConfig && (
                                            <div className="action-buttons">
                                                <KatButton variant="secondary"
                                                    label={reviewDiffLoading && reviewDiffChangeId === row.changeId ? 'Loading...' : 'Diff'}
                                                    onClick={() => handleViewDiff(row)}
                                                    disabled={reviewDiffLoading} />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Reject Confirmation Modal ── */}
            {showRejectModal && (
                <div className="dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="reject-modal-title"
                    tabIndex={-1} ref={focusOnMount}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') setShowRejectModal(null); }}>
                    <div className="dialog">
                        <h3 id="reject-modal-title">Reject Change Request</h3>
                        <p>Are you sure you want to reject this request?</p>
                        <div className="dialog-actions">
                            <KatButton variant="secondary" label="Cancel"
                                onClick={() => setShowRejectModal(null)} />
                            <KatButton variant="danger"
                                    label={actionLoading ? 'Rejecting...' : 'Confirm Reject'}
                                    onClick={() => {
                                        const row = deployerRows.find(r => r.changeId === showRejectModal);
                                        if (row) handleReject(row.changeId, row.requestor);
                                    }}
                                    disabled={actionLoading !== null} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Review Diff Modal ── */}
            {reviewDiffChangeId && !reviewDiffLoading && (
                <div className="dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="diff-modal-title"
                    tabIndex={-1} ref={focusOnMount}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape') closeDiffModal(); }}>
                    <div className="dialog dialog-wide">
                        <h3 id="diff-modal-title">Config Diff — Current (v{deployerRows.find(r => r.changeId === reviewDiffChangeId)?.fromVersion ?? '?'}) vs Proposed</h3>
                        <div className="json-diff-container">
                            <JsonDiffView oldData={reviewDiffOldData} newData={reviewDiffNewData} />
                        </div>
                        <div className="dialog-actions">
                            <KatButton variant="secondary" label="Close" onClick={closeDiffModal} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ConfigDeployerTab;
